import { useEffect, useMemo, useState } from 'react';
import { supChatBotReportFun } from '../../../api/supportAdmin';
import './chatbotReport.css';

const DAY = 24 * 60 * 60 * 1000;
const ymd = (d) => {
    const x = new Date(d);
    return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '');
const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);
const ago = (d) => {
    if (!d) return '';
    const m = Math.round((Date.now() - new Date(d).getTime()) / 60000);
    if (m < 60) return `${Math.max(m, 1)}m ago`;
    if (m < 1440) return `${Math.round(m / 60)}h ago`;
    return `${Math.round(m / 1440)}d ago`;
};

const RANGES = [
    { key: '7', label: 'Last 7 days', days: 7 },
    { key: '30', label: 'Last 30 days', days: 30 },
    { key: '90', label: 'Last 90 days', days: 90 },
];

// Plain-language tips shown under the biggest drop in the funnel
const DROP_TIPS = {
    sawTrips: 'People start chatting but leave before seeing trips. Keep the first questions short.',
    interested: 'People see trips but don’t show interest. Reply fast and call warm leads.',
    booked: 'Interested people aren’t booking yet. Call them from the list below.',
};

/**
 * Chatbot performance report: how many chats saw trips, showed interest
 * and booked, who to call first, and which destinations work best.
 */
export default function ChatbotReportModal({ open, onClose, fromDate, toDate, assignedSupportId, onOpenLead }) {
    const pageRange = fromDate || toDate;
    const [range, setRange] = useState(pageRange ? 'page' : '30');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) setRange(pageRange ? 'page' : '30');
    }, [open, pageRange]);

    const params = useMemo(() => {
        if (range === 'page') return { fromDate, toDate, assignedSupportId };
        const days = RANGES.find((r) => r.key === range)?.days || 30;
        return { fromDate: ymd(Date.now() - (days - 1) * DAY), toDate: ymd(Date.now()), assignedSupportId };
    }, [range, fromDate, toDate, assignedSupportId]);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                setError('');
                const res = await supChatBotReportFun(params);
                if (!cancelled) setData(res.data);
            } catch (e) {
                if (!cancelled) setError(e?.response?.data?.message || 'Could not load the report.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, params]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => e.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    const t = data?.totals || {};
    const funnel = data?.funnel || [];
    // Chat leads on the website (from the bot's personal links)
    const webFunnel = data?.webFunnel || [];
    const WEB_FILL = { chats: 'chats', openedLink: 'sawTrips', pickedDate: 'interested', checkout: 'interested', paid: 'booked' };

    // Biggest drop between two funnel steps (only when there is data)
    let drop = null;
    for (let i = 1; i < funnel.length; i++) {
        const prev = funnel[i - 1].count;
        if (!prev) continue;
        const kept = pct(funnel[i].count, prev);
        if (!drop || kept < drop.kept) drop = { from: funnel[i - 1], to: funnel[i], kept };
    }

    const kpis = [
        {
            label: 'Chats',
            value: t.chats || 0,
            note: `${t.newChats || 0} new · WhatsApp ${t.whatsapp || 0} · Instagram ${t.instagram || 0}`,
            tone: 'blue',
        },
        {
            label: 'Saw trips',
            value: t.sawTrips || 0,
            note: `${pct(t.sawTrips, t.chats)}% of chats`,
            tone: 'indigo',
        },
        {
            label: 'Interested 🔥',
            value: t.interested || 0,
            note: `${pct(t.interested, t.sawTrips)}% of people who saw trips`,
            tone: 'orange',
        },
        {
            label: 'Booked ✅',
            value: t.booked || 0,
            note: `${pct(t.booked, t.interested)}% of interested people`,
            tone: 'green',
        },
    ];

    const quality = [
        { label: 'Hot', value: t.hot || 0, cls: 'hot' },
        { label: 'Warm', value: t.warm || 0, cls: 'warm' },
        { label: 'No reply', value: t.cold || 0, cls: 'cold' },
        { label: 'Not interested', value: t.notInterested || 0, cls: 'no' },
        { label: 'Unsubscribed', value: t.optedOut || 0, cls: 'no' },
    ];
    const travel = [
        { label: '📅 This weekend', value: t.travelWeekend || 0 },
        { label: '🗓️ This month', value: t.travelMonth || 0 },
        { label: '👀 Just exploring', value: t.travelExploring || 0 },
    ];
    const travelMax = Math.max(1, ...travel.map((x) => x.value));
    const destMax = Math.max(1, ...(data?.destinations || []).map((d) => d.chats));
    const sourceMax = Math.max(1, ...(data?.hotBySource || []).map((s) => s.count));

    return (
        <div className='cbr-overlay' onClick={onClose}>
            <div className='cbr-modal' role='dialog' aria-label='Chatbot report' onClick={(e) => e.stopPropagation()}>
                {/* ── Header ── */}
                <div className='cbr-header'>
                    <div>
                        <h2>Chatbot performance</h2>
                        <p className='cbr-sub'>
                            {data?.range
                                ? `${fmtDate(data.range.from)} – ${fmtDate(data.range.to)} · people who chatted in this period`
                                : 'Loading…'}
                        </p>
                    </div>
                    <button className='cbr-close' onClick={onClose} aria-label='Close'>
                        ✕
                    </button>
                </div>

                <div className='cbr-ranges'>
                    {RANGES.map((r) => (
                        <button
                            key={r.key}
                            className={range === r.key ? 'active' : ''}
                            onClick={() => setRange(r.key)}
                        >
                            {r.label}
                        </button>
                    ))}
                    {pageRange && (
                        <button className={range === 'page' ? 'active' : ''} onClick={() => setRange('page')}>
                            Page dates
                        </button>
                    )}
                </div>

                {error && <p className='cbr-error'>{error}</p>}
                {loading && !data && <div className='cbr-skeleton' />}

                {data && (
                    <div className={`cbr-body ${loading ? 'is-loading' : ''}`}>
                        {/* ── KPI cards ── */}
                        <div className='cbr-kpis'>
                            {kpis.map((k) => (
                                <div key={k.label} className={`cbr-kpi cbr-${k.tone}`}>
                                    <span className='cbr-kpi-label'>{k.label}</span>
                                    <span className='cbr-kpi-value'>{k.value}</span>
                                    <span className='cbr-kpi-note'>{k.note}</span>
                                </div>
                            ))}
                        </div>

                        {/* ── Funnel ── */}
                        <section className='cbr-card'>
                            <div className='cbr-card-head'>
                                <h3>From chat to booking</h3>
                                <span>How many people reached each step</span>
                            </div>
                            <div className='cbr-funnel'>
                                {funnel.map((f, i) => {
                                    const prev = i ? funnel[i - 1].count : 0;
                                    return (
                                        <div key={f.key} className='cbr-step'>
                                            {i > 0 && (
                                                <div className='cbr-step-kept'>
                                                    ↓ {pct(f.count, prev)}% continued
                                                </div>
                                            )}
                                            <div className='cbr-step-row'>
                                                <span className='cbr-step-name'>{f.step}</span>
                                                <div className='cbr-track'>
                                                    <div
                                                        className={`cbr-fill cbr-fill-${f.key}`}
                                                        style={{ width: `${Math.max(f.count ? 4 : 0, f.pct)}%` }}
                                                    />
                                                </div>
                                                <span className='cbr-step-count'>{f.count}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            {drop && drop.kept < 100 && (
                                <div className='cbr-tip'>
                                    <b>
                                        💡 Biggest drop: {drop.from.step} → {drop.to.step} (only {drop.kept}% moved on)
                                    </b>
                                    <span>{DROP_TIPS[drop.to.key]}</span>
                                </div>
                            )}
                        </section>

                        {/* ── Website funnel (chat leads who opened the bot's links) ── */}
                        {webFunnel.length > 0 && (
                            <section className='cbr-card'>
                                <div className='cbr-card-head'>
                                    <h3>On the website</h3>
                                    <span>What chat leads did after opening the bot's trip links</span>
                                </div>
                                <div className='cbr-funnel'>
                                    {webFunnel.map((f, i) => {
                                        const prev = i ? webFunnel[i - 1].count : 0;
                                        return (
                                            <div key={f.key} className='cbr-step'>
                                                {i > 0 && (
                                                    <div className='cbr-step-kept'>↓ {pct(f.count, prev)}% continued</div>
                                                )}
                                                <div className='cbr-step-row'>
                                                    <span className='cbr-step-name'>{f.step}</span>
                                                    <div className='cbr-track'>
                                                        <div
                                                            className={`cbr-fill cbr-fill-${WEB_FILL[f.key] || 'chats'}`}
                                                            style={{ width: `${Math.max(f.count ? 4 : 0, f.pct)}%` }}
                                                        />
                                                    </div>
                                                    <span className='cbr-step-count'>{f.count}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </section>
                        )}

                        {/* ── Who to call ── */}
                        <section className='cbr-card'>
                            <div className='cbr-card-head'>
                                <h3>📞 Call these leads first</h3>
                                <span>Interested, not booked yet · click to open</span>
                            </div>
                            {data.actionLeads?.length ? (
                                <div className='cbr-leads'>
                                    {data.actionLeads.map((l) => (
                                        <button
                                            key={l._id}
                                            className='cbr-lead'
                                            onClick={() => {
                                                onOpenLead?.(l._id);
                                                onClose();
                                            }}
                                        >
                                            <div className='cbr-lead-main'>
                                                <span className='cbr-lead-name'>
                                                    {l.platform === 'instagram' ? '📸' : '💬'} {l.name || l.phoneNumber || 'Guest'}
                                                </span>
                                                <span className='cbr-lead-meta'>
                                                    {[l.place, l.seats ? `${l.seats} seats` : '', l.travel]
                                                        .filter(Boolean)
                                                        .join(' · ') || 'No trip picked yet'}
                                                </span>
                                            </div>
                                            <div className='cbr-lead-side'>
                                                <span className='cbr-chip'>{l.reason}</span>
                                                <span className='cbr-lead-meta'>
                                                    {l.assignedTo || 'Unassigned'} · {ago(l.hotAt || l.lastMessageAt)}
                                                </span>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <p className='cbr-empty'>No one waiting — all interested leads are handled 🎉</p>
                            )}
                        </section>

                        {/* ── Destinations ── */}
                        <section className='cbr-card'>
                            <div className='cbr-card-head'>
                                <h3>Top destinations</h3>
                                <span>What people picked in the chat</span>
                            </div>
                            {data.destinations?.length ? (
                                <table className='cbr-table'>
                                    <thead>
                                        <tr>
                                            <th>Destination</th>
                                            <th>Chats</th>
                                            <th>Interested</th>
                                            <th>Booked</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.destinations.map((d) => (
                                            <tr key={d.destination}>
                                                <td>
                                                    <div className='cbr-dest'>
                                                        <span>{d.destination}</span>
                                                        <div className='cbr-mini'>
                                                            <div style={{ width: `${(d.chats / destMax) * 100}%` }} />
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>{d.chats}</td>
                                                <td>{d.interested || '–'}</td>
                                                <td>{d.booked || '–'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p className='cbr-empty'>No destinations picked yet.</p>
                            )}
                        </section>

                        {/* ── Small cards ── */}
                        <div className='cbr-trio'>
                            <section className='cbr-card'>
                                <h4>When they want to travel</h4>
                                {travel.map((x) => (
                                    <div key={x.label} className='cbr-hbar'>
                                        <span>{x.label}</span>
                                        <div className='cbr-mini'>
                                            <div style={{ width: `${(x.value / travelMax) * 100}%` }} />
                                        </div>
                                        <b>{x.value}</b>
                                    </div>
                                ))}
                                <p className='cbr-foot'>👥 Groups of 4+: <b>{t.groupLeads || 0}</b></p>
                            </section>

                            <section className='cbr-card'>
                                <h4>Lead quality (bot)</h4>
                                {quality.map((q) => (
                                    <div key={q.label} className='cbr-qrow'>
                                        <span className={`cbr-dot cbr-dot-${q.cls}`} />
                                        <span>{q.label}</span>
                                        <b>{q.value}</b>
                                    </div>
                                ))}
                            </section>

                            <section className='cbr-card'>
                                <h4>What made leads hot</h4>
                                {data.hotBySource?.length ? (
                                    data.hotBySource.map((s) => (
                                        <div key={s.source} className='cbr-hbar cbr-hbar-wide'>
                                            <span>{s.label}</span>
                                            <div className='cbr-mini cbr-mini-hot'>
                                                <div style={{ width: `${(s.count / sourceMax) * 100}%` }} />
                                            </div>
                                            <b>{s.count}</b>
                                        </div>
                                    ))
                                ) : (
                                    <p className='cbr-empty'>No hot leads from the bot yet.</p>
                                )}
                                {data.hotByFollowUp?.length > 0 && (
                                    <p className='cbr-foot'>
                                        Best follow-up:{' '}
                                        <b>{[...data.hotByFollowUp].sort((a, b) => b.count - a.count)[0].label}</b>
                                    </p>
                                )}
                            </section>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
